// Dicionário Adaptativo por Tipo de Pessoa e Modo de Vocabulário (Modo Simplificado vs. Modo Jargão Técnico):
// Adapta automaticamente títulos, subtítulos, resumos e impacto operacional conforme quem está acessando
// (Médico, Enfermeiro, Financeiro, Técnico em TI ou Parceiro Externo) e conforme o nível de detalhe escolhido
// (Modo Simplificado vs. Modo Jargão Técnico).

export type ExperienceMode = 'plain' | 'expert';

export type PersonaLens = 'clinical' | 'operations' | 'financial' | 'external' | 'admin';

export interface GlossaryEntry {
  readonly key: string;
  readonly plainTitle: string;
  readonly plainSummary: string;
  readonly expertTitle: string;
  readonly expertSummary: string;
  readonly expertCode: string;
  readonly personaWhyItMatters: Readonly<Record<PersonaLens, string>>;
  readonly personaExpertWhyItMatters: Readonly<Record<PersonaLens, string>>;
}

export interface PersonaModeHeaderCopy {
  readonly plainTitle: string;
  readonly expertTitle: string;
  readonly plainSubtitle: string;
  readonly expertSubtitle: string;
  readonly plainGuidance: string;
  readonly expertGuidance: string;
}

export const PORTAL_GLOSSARY: Readonly<Record<string, GlossaryEntry>> = {
  harness2: {
    key: 'harness2',
    plainTitle: 'Base de Conhecimento de Contratos e Orçamentos',
    plainSummary:
      'O sistema registra aprendizados de contratos, cotações de fornecedores e desvios de custo para sugerir orçamentos mais precisos nos próximos estudos.',
    expertTitle: 'Memória Institucional de Contratos, Custos e Protocolos Clínicos',
    expertSummary:
      'Repositório estruturado de regras comerciais, clínicas e operacionais validadas pela diretoria para padronização de orçamentos e contratos.',
    expertCode: 'Base de Regras Homologadas',
    personaWhyItMatters: {
      clinical:
        'Lembra automaticamente exames e procedimentos médicos que costumam ficar ocultos no protocolo.',
      operations:
        'Evita esquecer taxas de arquivo, farmácia, reconsentimento e coleta domiciliar na rotina de enfermagem.',
      financial:
        'Compara a margem planejada no contrato com a margem real após pagar impostos e fornecedores.',
      external:
        'Garante que os pedidos e cotações sigam os padrões históricos homologados pela GIYUS.',
      admin:
        'Mantém o banco de regras institucionais organizado e validado com histórico de auditoria.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Audita notas de rodapé do Schedule of Assessments (SoA) contra o protocolo clínico ICH-GCP E6(R3) para impedir exames diagnósticos sem cobertura.',
      operations:
        'Cruza tempos de bancada, cadeia fria (-80°C), dispensação IWRS e reconsentimento TCLE com os gatilhos operacionais de cada visita clínica.',
      financial:
        'Reconcilia o desvio entre margem projetada (Gross-up 14,25% + Overhead 30%) e margem realizada pós conciliação e variação cambial PTAX.',
      external:
        'Padroniza cláusulas de SLA B2B, escopo de ordens de serviço e tetos de reembolso Pass-Through.',
      admin:
        'Centraliza as diretrizes corporativas aprovadas pela administração para aplicação consistente em novos estudos.',
    },
  },
  azureFunctions: {
    key: 'azureFunctions',
    plainTitle: 'Monitoramento Automático Diário',
    plainSummary:
      'Rotinas automáticas que verificam vencimentos de calibração de equipamentos, novos e-mails de contratos e atualizações de preços por localidade.',
    expertTitle: 'Rotinas Automatizadas de Calibração, Depreciação, Caixas de E-mail e Preços',
    expertSummary:
      'Verificação contínua de certificados RBC/ISO 17025, depreciação patrimonial, caixas compartilhadas corporativas e tabelas regionais de procedimentos.',
    expertCode: 'Monitoramento Operacional Contínuo',
    personaWhyItMatters: {
      clinical: 'Impede que um paciente seja atendido com equipamento cuja calibração venceu.',
      operations:
        'Organiza os e-mails que chegam nas caixas compartilhadas e avisa pendências da enfermaria.',
      financial:
        'Recalcula mensalmente a depreciação dos equipamentos e o custo por hora de uso nos estudos.',
      external:
        'Avisa com antecedência quando um certificado ou alvará do fornecedor precisa ser renovado.',
      admin: 'Monitora a execução das rotinas operacionais e alertas preventivos das unidades.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Bloqueia preventivamente o agendamento de visitas SoA caso equipamentos críticos (Espirometria, FeNO, ECG, Freezer -80°C) apresentem certificado RBC vencido.',
      operations:
        'Dispara alertas de reposição FEFO, janelas de coleta biológica IATA e triagem automática de mensagens nas caixas @giyus.com.br.',
      financial:
        'Atualiza o custo/hora depreciado por ativo (PAT/COM) e recalcula o impacto na margem líquida por procedimento executado.',
      external:
        'Notifica fornecedores homologados sobre expiração iminente de certidões e certificados de calibração rastreáveis.',
      admin:
        'Consolida indicadores operacionais de equipamentos, estoques, caixas de e-mail e custos regionais.',
    },
  },
  rls: {
    key: 'rls',
    plainTitle: 'Privacidade e Acesso Separado por Unidade ou Área',
    plainSummary:
      'Cada centro de pesquisa, médico, enfermeiro, financeiro ou parceiro vê os dados necessários ao seu trabalho, protegendo informações sensíveis e sigilosas.',
    expertTitle: 'Controle de Acesso Multi-Unidade e Segregação por Perfil',
    expertSummary:
      'Política de autorização por escopo organizacional e perfil funcional aplicada em todos os módulos operacionais e financeiros.',
    expertCode: 'Escopo Rede · Centros · Parceiros B2B',
    personaWhyItMatters: {
      clinical: 'Médicos acessam protocolos, exames e pacientes com foco clínico e sigilo ético.',
      operations:
        'A equipe de enfermagem e coordenação foca nos estudos e agendas da sua própria unidade.',
      financial:
        'A equipe financeira visualiza contratos, notas fiscais e margens consolidados com segurança.',
      external:
        'Parceiros acessam apenas as cotações, coletas ou protocolos em que foram convidados.',
      admin: 'Garante que nenhum usuário acesse ou altere dados fora das permissões do seu cargo.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Protege dados sensíveis de sujeitos de pesquisa sob LGPD/ICH-GCP e restringe aprovações clínicas a profissionais com registro de classe (CRM) ativo no DoA.',
      operations:
        'Segrega a operação por centro de pesquisa e valida habilitação COREN/CRF/CRBM para execução de procedimentos.',
      financial:
        'Isola razão contábil AP/AR, tabelas de preço da Holding e alçadas de aprovação de pedidos de compra.',
      external:
        'Aplica escopo B2B restrito por fornecedor e estudo para usuários de domínios externos.',
      admin:
        'Aplica permissões granulares por unidade, fornecedor e matriz de ações (Visualizar, Criar, Editar, Excluir, Aprovar, Exportar).',
    },
  },
  sod: {
    key: 'sod',
    plainTitle: 'Conferência Dupla Obrigatória (2 Pessoas Diferentes)',
    plainSummary:
      'Para evitar erros, quem cadastra um fornecedor, pedido ou certificado de calibração nunca pode aprovar sozinho — uma segunda pessoa precisa conferir.',
    expertTitle: 'Segregação de Funções (Dupla Conferência Obrigatória)',
    expertSummary:
      'Regra de governança que exige usuários distintos entre o responsável pelo cadastro e o responsável pela aprovação.',
    expertCode: 'Auditoria em 2 Etapas · Dupla Assinatura',
    personaWhyItMatters: {
      clinical:
        'Garante que equipamentos médicos críticos foram conferidos por duas pessoas antes do uso no paciente.',
      operations: 'Protege a enfermagem e coordenação contra falhas em inspeções e auditorias.',
      financial: 'Impede pagamentos ou liberações de compra sem conferência de uma segunda pessoa.',
      external: 'Seus documentos enviados passam por dupla conferência interna antes da liberação.',
      admin:
        'Impede que a mesma pessoa cadastre e aprove unilateralmente pedidos, fornecedores ou certificados.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Assegura dupla verificação (21 CFR Part 11 / Boas Práticas Clínicas) na liberação de equipamentos diagnósticos e cadeia fria.',
      operations:
        'Mantém rastreabilidade de quem registrou e quem conferiu lotes de kits, certificados RBC e termos regulatórios.',
      financial:
        'Garante governança na homologação de fornecedores, adjudicação de cotações e liberação de pagamentos.',
      external:
        'Fornece protocolo auditável de recebimento e dupla validação técnica de laudos e notas fiscais.',
      admin:
        'Garante rastreabilidade completa de autoria e aprovação com registro de auditoria imutável.',
    },
  },
  fefo: {
    key: 'fefo',
    plainTitle: 'Controle de Validade de Kits (Usar Primeiro o que Vence Antes)',
    plainSummary:
      'Organiza os kits de coleta e materiais médicos por data de validade para nenhum item vencer na prateleira e avisa quando o estoque está acabando.',
    expertTitle: 'Gestão de Estoque Clínico por Validade (FEFO) & Ponto de Pedido',
    expertSummary:
      'Alocação de lotes clínicos por prioridade de vencimento com alerta automático de reposição quando o saldo atinge o estoque mínimo.',
    expertCode: 'Controle de Lotes FEFO · Reposição Preventiva',
    personaWhyItMatters: {
      clinical: 'Garante que toda consulta de paciente tenha material válido e pronto para uso.',
      operations: 'Mostra para a enfermagem exatamente qual caixa e lote deve ser usado primeiro.',
      financial:
        'Evita prejuízo com descarte de materiais vencidos e compras de última hora mais caras.',
      external: 'Permite programar entregas de reposição antes de faltar material no centro.',
      admin: 'Conecta a baixa de estoque da visita diretamente ao aviso de reposição em compras.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Impede desvios de protocolo por coleta em tubos/kits expirados durante visitas críticas de PK/PD e biomarcadores.',
      operations:
        'Ordena lotes na bancada de enfermagem por data de expiração (FEFO) e vincula a baixa à visita do protocolo.',
      financial:
        'Mitiga perda patrimonial de insumos Pass-Through e dispara cotação preventiva respeitando o prazo de entrega do fornecedor.',
      external:
        'Sincroniza previsão de demanda por protocolo com o prazo de reposição do fornecedor homologado.',
      admin: 'Integra o saldo físico por centro com a geração de requisições no módulo de compras.',
    },
  },
  threeWayMatch: {
    key: 'threeWayMatch',
    plainTitle: 'Conferência de Pagamento e Resultado Financeiro do Estudo',
    plainSummary:
      'Antes de liberar qualquer pagamento, o sistema confere se o Contrato, o Pedido de Compra e a Nota Fiscal batem, mostrando o resultado líquido da clínica.',
    expertTitle: 'Conciliação Tripla (Pedido × Execução × Nota Fiscal), Câmbio PTAX & Margem Real',
    expertSummary:
      'Cruzamento entre Ordem de Compra, Comprovante de Execução Clínica e Nota Fiscal confrontado com a receita liquidada pelo patrocinador.',
    expertCode: 'Conciliação AP/AR · PTAX BCB · Margem Realizada',
    personaWhyItMatters: {
      clinical:
        'Garante que os repasses médicos e exames externos estejam cobertos pelo orçamento do estudo.',
      operations:
        'Confirma que o exame ou coleta foi realmente entregue antes de o financeiro pagar.',
      financial:
        'Cruza tudo o que recebemos do patrocinador com o que pagamos aos fornecedores e impostos.',
      external: 'Mostra com clareza o andamento da sua Nota Fiscal até a data do pagamento.',
      admin:
        'Registra todas as conferências financeiras sem divergência entre compras, clínica e contabilidade.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Assegura lastro orçamentário para honorários de PI/Sub-I e exames diagnósticos terceirizados previstos na grade SoA.',
      operations:
        'Exige manifesto de coleta ou laudo clínico validado pela coordenação como pré-condição de liquidação financeira.',
      financial:
        'Apura a margem líquida realizada por protocolo descontando tributos (14,25% / 16,33%), retenções (6,15%), Holdback (10%) e câmbio PTAX.',
      external:
        'Agiliza a liberação de pagamento após validação de NF-e, certidões negativas e comprovante de execução.',
      admin:
        'Consolida o razão de Contas a Pagar e Contas a Receber por protocolo clínico e centro de custo.',
    },
  },
  quoteComparison: {
    key: 'quoteComparison',
    plainTitle: 'Comparação de 3 Orçamentos e Tempo de Resposta do Fornecedor',
    plainSummary:
      'Coloca propostas de fornecedores lado a lado somando preço, frete e tempo de resposta para ajudar a escolher a opção mais rápida e econômica.',
    expertTitle: 'Mapa Comparativo de 3 Cotações (Custo Total & SLA de Resposta)',
    expertSummary:
      'Matriz de decisão que pondera Custo Total (preço + frete + taxas) e tempo médio de resposta/mobilização por serviço clínico.',
    expertCode: 'Comparativo 3 Cotações · SLA de Resposta',
    personaWhyItMatters: {
      clinical:
        'Prioriza fornecedores que respondem rápido quando há paciente aguardando exame ou coleta.',
      operations:
        'Elimina planilhas manuais na hora de cotar transporte de amostras, kits ou calibração.',
      financial:
        'Mostra na hora qual fornecedor tem o melhor preço total e se está dentro do teto do estudo.',
      external:
        'Critérios transparentes de escolha baseados em preço, prazo de resposta e qualidade.',
      admin: 'Gera o pedido de compra vinculado ao estudo assim que a melhor proposta é aprovada.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Evita perda de janela de visita selecionando fornecedores com atendimento prioritário em até 24h para pacientes em triagem.',
      operations:
        'Calcula o prazo total (cotação + mobilização em horas) por categoria crítica (Kits PK/PD, Gelo Seco IATA, Eco/RM, Calibração RBC).',
      financial:
        'Confronta o custo total das 3 propostas com a Tabela Oficial da Holding e controla o teto Pass-Through.',
      external:
        'Classifica objetivamente os parceiros pelo histórico de SLA cumprido e competitividade comercial.',
      admin:
        'Padroniza o processo de seleção de fornecedores com justificativa auditável e geração de ordem de compra.',
    },
  },
  closeOutLock: {
    key: 'closeOutLock',
    plainTitle: 'Trava de Encerramento do Estudo (Conferência de Equipamentos)',
    plainSummary:
      'Não permite encerrar um estudo clínico enquanto houver equipamento emprestado pelo patrocinador pendente de devolução ou destinação final.',
    expertTitle: 'Controle de Encerramento de Estudo (Close-Out) por Destinação de Comodato',
    expertSummary:
      'Validação que impede o encerramento administrativo do estudo enquanto existirem ativos em comodato sem comprovante de devolução, doação ou descarte.',
    expertCode: 'Controle Close-Out · Destinação de Comodato',
    personaWhyItMatters: {
      clinical: 'Garante que o estudo seja encerrado sem pendências perante o patrocinador.',
      operations:
        'Lista exatamente quais aparelhos emprestados ainda precisam ser devolvidos pela equipe.',
      financial:
        'Evita retenção da parcela final de pagamento do estudo por falta de devolução de aparelhos.',
      external: 'Registra formalmente a retirada dos equipamentos que estavam no centro.',
      admin:
        'Impede o fechamento do estudo enquanto houver equipamento em comodato sem baixa documentada.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Assegura conformidade regulatória na visita de encerramento (Close-Out Visit — COV) junto ao patrocinador e CEP/CONEP.',
      operations:
        'Exige baixa documental de todos os ativos em comodato (RMA de exportação, transferência autorizada ou certificado de destruição).',
      financial:
        'Desbloqueia o faturamento dos 10% de Holdback retidos contratualmente até o encerramento formal do centro.',
      external:
        'Emite manifesto auditável de coleta reversa e encerramento de responsabilidade sobre o ativo.',
      admin:
        'Garante que nenhum equipamento de terceiros permaneça sem destinação formal após o término do protocolo.',
    },
  },
  gedEtmf: {
    key: 'gedEtmf',
    plainTitle: 'Arquivo Digital de Documentos do Estudo com Assinatura',
    plainSummary:
      'Guarda com segurança os documentos obrigatórios do estudo (Termo de Consentimento, Delegações da Equipe, Contratos e Certificados) com assinatura eletrônica.',
    expertTitle:
      'GED / eTMF Controlado, Assinatura Eletrônica 21 CFR Part 11 / RDC 945 & Guarda 25 Anos',
    expertSummary:
      'Repositório regulatório versionado com verificação criptográfica SHA-256, confirmação de identidade na assinatura e retenção de longo prazo.',
    expertCode: 'GED / eTMF · SHA-256 · Guarda Regulatória 25 Anos',
    personaWhyItMatters: {
      clinical:
        'Garante que nenhuma consulta use versão desatualizada do Termo de Consentimento (TCLE).',
      operations:
        'Permite à enfermagem emitir rapidamente o guia de visita do paciente e conferir documentos vigentes.',
      financial:
        'Mantém contratos, aditivos e notas fiscais arquivados com segurança para auditorias.',
      external: 'Permite conferir a validade de qualquer guia ou certificado emitido pelo centro.',
      admin:
        'Garante integridade de arquivos com código de segurança e bloqueio contra exclusão indevida.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Controla vigência de versões de TCLE/ICF aprovadas pelo CEP/CONEP e assinaturas do Delegation of Authority Log (DoA).',
      operations:
        'Automatiza geração de Source Templates e guias de encaminhamento clínico vinculadas à versão corrente do protocolo.',
      financial:
        'Preserva minutas CTA, memórias de cálculo e comprovantes fiscais sob classe de retenção legal.',
      external: 'Disponibiliza verificação pública de integridade documental via hash SHA-256.',
      admin:
        'Aplica retenção regulatória, bloqueio jurídico (Legal Hold) e carimbo de integridade em cada versão.',
    },
  },
  deterministicScheduling: {
    key: 'deterministicScheduling',
    plainTitle: 'Agenda Clínica com Validação de Regras do Protocolo',
    plainSummary:
      'Antes de confirmar a visita de um participante, o sistema confere se a data está no prazo do estudo, se o médico e o enfermeiro estão disponíveis, se o aparelho está calibrado e se há kit válido.',
    expertTitle:
      'Agendamento Clínico Protocolar (Janela SoA, Equipe Habilitada, Calibração RBC e Kit FEFO)',
    expertSummary:
      'Validação simultânea de janela de visita do protocolo, disponibilidade de sala e equipe, calibração vigente de equipamentos e validade de kits antes da confirmação da agenda.',
    expertCode: 'Agenda SoA · Validação Clínica em 6 Etapas',
    personaWhyItMatters: {
      clinical:
        'Evita que pacientes sejam agendados fora do prazo do protocolo ou sem médico habilitado.',
      operations:
        'Organiza salas, poltronas de infusão e horários da enfermagem sem conflito de agenda.',
      financial:
        'Ao concluir a visita, já registra o procedimento realizado para cobrança do patrocinador.',
      external: 'Confirma se o laboratório ou clínica de imagem parceira atende na data agendada.',
      admin: 'Impede conflitos de horário ou agendamentos fora de conformidade regulatória.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Elimina desvios de janela protocolar (Out-of-Window Protocol Deviation) e valida delegação DoA vigente do PI/Sub-I.',
      operations:
        'Verifica disponibilidade simultânea de sala/poltrona, profissional COREN/CRF habilitado, equipamento com RBC válido e lote FEFO.',
      financial:
        'Integra o check-out da visita SoA ao acumulador de faturamento clínico e baixa de estoque.',
      external:
        'Sincroniza janelas de coleta e exames diagnósticos externos com a grade horária dos parceiros homologados.',
      admin:
        'Assegura conformidade operacional em todas as marcações de visitas dos centros de pesquisa.',
    },
  },
  portabilityExport: {
    key: 'portabilityExport',
    plainTitle: 'Previsão de Capacidade do Centro e Exportação para Auditoria',
    plainSummary:
      'Mostra com antecedência se faltarão salas, poltronas ou horas de equipe para os estudos e permite baixar um pacote completo de dados para fiscalizações.',
    expertTitle: 'Planejamento de Capacidade (90 Dias) & Exportação Auditável SHA-256',
    expertSummary:
      'Projeção de ocupação de recursos clínicos para 90 dias combinada com geração de pacote ZIP assinado contendo manifesto criptográfico verificável.',
    expertCode: 'Capacidade 90d · Pacote de Auditoria SHA-256',
    personaWhyItMatters: {
      clinical:
        'Mostra se a equipe médica e o centro têm espaço na agenda antes de aceitar um novo estudo.',
      operations:
        'Ajuda a enfermagem a planejar semanas de pico de atendimento e ter cópia de segurança diária.',
      financial: 'Cruza a ocupação das salas e equipe com a previsão de faturamento dos estudos.',
      external:
        'Permite que monitores e auditores confiram os documentos exportados com total transparência.',
      admin:
        'Gera pacotes completos de backup e portabilidade com verificação automática de integridade.',
    },
    personaExpertWhyItMatters: {
      clinical:
        'Previne sobrecarga de horas de investigação (PI/Sub-I) e gargalos em procedimentos de longa duração (infusão/PK 24h).',
      operations:
        'Dimensiona carga de enfermagem, poltronas e processamento laboratorial ao longo do horizonte de 90 dias.',
      financial:
        'Correlaciona capacidade instalada dos centros com receita projetada e cumprimento de metas de recrutamento.',
      external:
        'Viabiliza inspeções remotas de patrocinadores, CROs, ANVISA e FDA com validação SHA-256 ponta a ponta.',
      admin:
        'Atende requisitos de portabilidade de dados, auditoria regulatória e contingência operacional.',
    },
  },
};

export const PERSONA_LENS_META: Readonly<
  Record<
    PersonaLens,
    {
      readonly label: string;
      readonly shortLabel: string;
      readonly codeTag: string;
      readonly icon: string;
      readonly description: string;
      readonly defaultMode: ExperienceMode;
    }
  >
> = {
  clinical: {
    label: 'Médico (Investigador Principal & Equipe Clínica)',
    shortLabel: 'Médico / Clínico (PI)',
    codeTag: 'MÉDICO',
    icon: 'MÉDICO',
    description:
      'Adapta os textos para avaliação médica do protocolo, consultas e exames por visita, segurança do paciente e responsabilidade clínica.',
    defaultMode: 'plain',
  },
  operations: {
    label: 'Enfermeiro (Enfermagem de Pesquisa & Coordenação)',
    shortLabel: 'Enfermeiro / Coordenação',
    codeTag: 'ENFERMEIRO',
    icon: 'ENFERMEIRO',
    description:
      'Adapta os textos para rotina de visitas, preparo e validade de kits de coleta, tempo de enfermaria, calibrações e andamento diário do estudo.',
    defaultMode: 'plain',
  },
  financial: {
    label: 'Financeiro (Orçamentos, Faturamento & Compras)',
    shortLabel: 'Financeiro · Compras & Finanças',
    codeTag: 'FINANCEIRO',
    icon: 'FINANCEIRO',
    description:
      'Adapta os textos para orçamento do contrato, impostos, câmbio, comparação de cotações, tempo de resposta de fornecedor e margem líquida.',
    defaultMode: 'plain',
  },
  admin: {
    label: 'Diretoria & Administração Master',
    shortLabel: 'Administrador Master',
    codeTag: 'ADMIN',
    icon: 'ADMIN',
    description:
      'Visão executiva e administrativa completa de estudos clínicos, contratos, fornecedores, compras, contabilidade e governança de acessos.',
    defaultMode: 'plain',
  },
  external: {
    label: 'Parceiro Externo (Patrocinador, CRO ou Fornecedor)',
    shortLabel: 'Parceiro Externo',
    codeTag: 'EXTERNO',
    icon: 'EXTERNO',
    description:
      'Adapta os textos para acompanhamento de cotações, coletas autorizadas, envio de notas fiscais e protocolos vinculados.',
    defaultMode: 'plain',
  },
};

export const MODULE_PERSONA_HEADERS: Readonly<
  Record<string, Readonly<Record<PersonaLens, PersonaModeHeaderCopy>>>
> = {
  studies: {
    clinical: {
      plainTitle: 'Estudos Clínicos, Contratos & Orçamentos',
      expertTitle:
        'Diretoria Médica & PI — Grade SoA, Elegibilidade ICH-GCP E6(R3) & Cobertura Clínica',
      plainSubtitle:
        'Consulte visitas, procedimentos médicos e cobertura contratual de cada protocolo clínico.',
      expertSubtitle:
        'Auditoria clínica do Schedule of Assessments (SoA), tempo de hora-médica (PI/Sub-I), critérios de elegibilidade e cláusulas de proteção a participantes.',
      plainGuidance:
        'Confira as visitas do protocolo, os exames obrigatórios do participante e os pontos de atenção clínicos.',
      expertGuidance:
        'Inspecione a matriz SoA × orçamento por visita, cobertura de Eventos Adversos Graves (SAE/SUSAR) e exequibilidade clínica.',
    },
    operations: {
      plainTitle: 'Estudos Clínicos, Contratos & Orçamentos',
      expertTitle:
        'Coordenação & Enfermagem de Pesquisa — Execução SoA, Janelas de Visita & Triagem',
      plainSubtitle:
        'Organize os e-mails recebidos de cada estudo, acompanhe os procedimentos por visita e consulte os centros de pesquisa.',
      expertSubtitle:
        'Gestão operacional de protocolos clínicos, processamento laboratorial IATA/FEFO, tempos de atendimento e resolução de dúvidas.',
      plainGuidance:
        'Vincule os e-mails ao estudo correspondente e acompanhe o roteiro operacional de cada visita.',
      expertGuidance:
        'Valide a carga operacional por visita SoA, exigências de cadeia fria (-20°C/-80°C) e janelas de triagem.',
    },
    financial: {
      plainTitle: 'Estudos Clínicos, Contratos & Orçamentos',
      expertTitle:
        'Controladoria & Finanças Clínicas — Orçamentos CTA, Impostos, PTAX & Fluxo de Caixa',
      plainSubtitle:
        'Compare o custo base de cada visita com o valor negociado com o patrocinador, confira impostos, tabelas por cidade e pagamentos.',
      expertSubtitle:
        'Conciliação econômico-financeira de contratos clínicos, formação de preço por visita SoA, reembolso Pass-Through e projeção de caixa.',
      plainGuidance:
        'Acompanhe a receita por visita e exame realizado, os impostos aplicáveis e a previsão financeira do estudo.',
      expertGuidance:
        'Audite o comparativo Baseline vs. Negociado, Tabela Oficial da Holding, liquidação cambial PTAX BCB e retenções contratuais.',
    },
    admin: {
      plainTitle: 'Estudos Clínicos, Contratos & Orçamentos',
      expertTitle: 'Gestão de Estudos Clínicos, Contratos, Orçamentos e Caixas Compartilhadas',
      plainSubtitle:
        'Gerencie estudos clínicos, dossiês contratuais, orçamentos por visita, tabelas de procedimentos e triagem de e-mails corporativos.',
      expertSubtitle:
        'Visão consolidada de protocolos clínicos, negociações contratuais, tabelas de procedimentos por localidade e regras de classificação.',
      plainGuidance:
        'Cadastre estudos, vincule mensagens das caixas @giyus.com.br e acompanhe os orçamentos e contratos da rede.',
      expertGuidance:
        'Supervisione os dossiês contratuais, tabelas de preços regionais, conciliações de reembolso e regras institucionais.',
    },
    external: {
      plainTitle: 'Estudos Clínicos: Protocolos Vinculados',
      expertTitle: 'Portal B2B de Estudos & Patrocinadores — Protocolos e Centros Vinculados',
      plainSubtitle:
        'Consulte os estudos autorizados para a sua organização com acesso direto às informações permitidas.',
      expertSubtitle:
        'Visualização controlada restrita aos protocolos e centros vinculados ao parceiro externo.',
      plainGuidance: 'Acompanhe os dados e prazos dos protocolos em que sua empresa atua.',
      expertGuidance: 'Consulte especificações aprovadas do protocolo sob controle de acesso.',
    },
  },
  suppliers: {
    clinical: {
      plainTitle: 'Fornecedores, Exames Externos e Validade de Kits',
      expertTitle:
        'Suporte Clínico & Diagnóstico Terceirizado — SLA de Resposta, Kits FEFO & Exames',
      plainSubtitle:
        'Consulte laboratórios e fornecedores homologados, tempos de resposta e validade dos kits de coleta.',
      expertSubtitle:
        'Avaliação de tempo de resposta e mobilização por serviço crítico (Kits PK/PD, Courier Gelo Seco IATA, Eco/RM, Calibração RBC).',
      plainGuidance:
        'Priorize fornecedores com resposta rápida para não atrasar exames nem perder o prazo de visita do paciente.',
      expertGuidance:
        'Utilize o indicador de Tempo de Resposta por serviço para proteger janelas de protocolo SoA.',
    },
    operations: {
      plainTitle: 'Fornecedores, Estoque de Kits e Agendamento de Coletas',
      expertTitle: 'Operações de Suprimentos Clínicos — Controle FEFO, Coletas & Ordens de Serviço',
      plainSubtitle:
        'Controle a validade dos kits clínicos, solicite reposição e acompanhe o agendamento de coletas e serviços.',
      expertSubtitle:
        'Gestão integrada de lotes FEFO por estudo, requisição por ponto de pedido, coletas biológicas e ordens de serviço clínico.',
      plainGuidance:
        'Confira os kits por ordem de vencimento, aprove coletas de amostras e acompanhe os fornecedores de cada unidade.',
      expertGuidance:
        'Monitore prazos de mobilização, manifestos logísticos de cadeia fria e rastreabilidade de lotes por estudo.',
    },
    financial: {
      plainTitle: 'Fornecedores, Compras, Cotações e Pagamentos',
      expertTitle:
        'Gestão de Suprimentos & Controladoria — Mapa de 3 Cotações, Pedidos & Conciliação',
      plainSubtitle:
        'Compare propostas de fornecedores combinando preço e prazo de resposta, aprove pedidos de compra e confira notas fiscais.',
      expertSubtitle:
        'Gestão completa de compras e faturamento: comparativo de 3 propostas, alçadas de dupla conferência, contas a pagar/receber e acumulador de cobranças.',
      plainGuidance:
        'Compare 3 orçamentos, avalie o prazo de atendimento e confira a nota fiscal antes da liberação financeira.',
      expertGuidance:
        'Audite o custo total por localidade, retenções fiscais, Holdback contratual e conciliação entre Pedido, Execução e Nota Fiscal.',
    },
    admin: {
      plainTitle: 'Fornecedores, Compras & Suprimentos Clínicos',
      expertTitle: 'Gestão de Fornecedores, Compras, Estoque Clínico e Contabilidade',
      plainSubtitle:
        'Cadastre e gerencie fornecedores homologados, tabelas de preços por cidade, estoques de kits clínicos, cotações e pedidos de compra.',
      expertSubtitle:
        'Administração centralizada do catálogo de fornecedores, cobertura geográfica por centro, ordens de serviço, compras e conciliação contábil.',
      plainGuidance:
        'Gerencie o cadastro de fornecedores, acompanhe cotações, aprove pedidos de compra e controle o estoque das unidades.',
      expertGuidance:
        'Supervisione o desempenho de atendimento dos fornecedores, aprovações em dupla conferência e conciliação financeira.',
    },
    external: {
      plainTitle: 'Portal do Fornecedor: Cotações, Coletas e Notas Fiscais',
      expertTitle: 'Portal B2B do Fornecedor Homologado — Cotações, Coletas & Faturamento',
      plainSubtitle:
        'Responda pedidos de orçamento, confirme datas de coleta nos centros e acompanhe a liberação das suas notas fiscais.',
      expertSubtitle:
        'Ambiente B2B dedicado ao envio de propostas comerciais, confirmação de coletas, certificados de calibração e notas fiscais.',
      plainGuidance:
        'Mantenha seu tempo de resposta atualizado para atendimento prioritário nas demandas da rede.',
      expertGuidance:
        'Cumpra os prazos acordados em contrato e anexe a documentação fiscal e técnica para liberação de pagamento.',
    },
  },
};
